import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { deriveKpiReferenceFromCharts, mapKpiDashboardResponse, mergeKpiReference, } from '../model/kpi-dashboard.model';
import { appendPageable, readPagedRows } from '../utils/spring-api';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const dashboardApi = API_ROUTES.reports.kpiDashboard;
const agentsApi = API_ROUTES.reports.kpiAgents;
const initialState = {
    loading: false,
    agentsLoading: false,
    errorMessage: null,
    reference: null,
    analytics: null,
    agents: {
        rows: [],
        total: 0,
    },
};
function buildFilterParams(params) {
    const q = new URLSearchParams();
    if (params.startDate)
        q.set('startDate.equals', params.startDate);
    if (params.endDate)
        q.set('endDate.equals', params.endDate);
    if (params.agents?.length)
        q.set('agents.in', params.agents.join(','));
    if (params.queues?.length)
        q.set('queues.in', params.queues.join(','));
    if (params.shifts?.length)
        q.set('shifts.in', params.shifts.join(','));
    return q;
}
function buildDashboardParams(params) {
    const q = buildFilterParams(params);
    if (params.chartType)
        q.set('chartType.equals', params.chartType);
    return q;
}
function buildAgentsQueryParams(params) {
    const q = buildFilterParams(params);
    appendPageable(q, params.page, params.size, params.sort);
    return q;
}
async function fetchKpiAgentsPage(params) {
    const qs = buildAgentsQueryParams(params).toString();
    const response = await axios.get(`${agentsApi}${qs ? `?${qs}` : ''}`);
    const { rows, total } = readPagedRows(response.data, response.headers);
    return { rows, total };
}
export async function fetchKpiAgentsRows(params) {
    return fetchKpiAgentsPage(params);
}
export const getKpiDashboardAnalytics = createAsyncThunk('kpiDashboard/analytics', async (params) => {
    const qs = buildDashboardParams(params).toString();
    const response = await axios.get(`${dashboardApi}${qs ? `?${qs}` : ''}`);
    return mapKpiDashboardResponse(response.data);
}, { serializeError: serializeAxiosError });
export const getKpiAgents = createAsyncThunk('kpiDashboard/agents', async (params) => {
    const { captureReference, ...query } = params;
    const pageResult = await fetchKpiAgentsPage(query);
    let referenceRows;
    if (captureReference && pageResult.total > pageResult.rows.length) {
        const all = await fetchKpiAgentsPage({
            ...query,
            page: 0,
            size: Math.max(pageResult.total, 1),
        });
        referenceRows = all.rows;
    }
    return {
        rows: pageResult.rows,
        total: pageResult.total,
        referenceRows,
    };
}, { serializeError: serializeAxiosError });
export const kpiDashboardSlice = createSlice({
    name: 'kpiDashboard',
    initialState: initialState,
    reducers: {
        clearKpiDashboard(state) {
            state.analytics = null;
            state.agents = { rows: [], total: 0 };
            state.errorMessage = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getKpiDashboardAnalytics.fulfilled, (state, action) => {
            state.loading = false;
            state.analytics = action.payload;
            state.reference = deriveKpiReferenceFromCharts(action.payload);
        })
            .addCase(getKpiAgents.fulfilled, (state, action) => {
            state.agentsLoading = false;
            state.agents = {
                rows: action.payload.rows,
                total: action.payload.total,
            };
            if (state.reference && action.payload.referenceRows?.length) {
                state.reference = mergeKpiReference(state.reference, action.payload.referenceRows);
            }
            else if (state.reference && action.payload.rows.length) {
                state.reference = mergeKpiReference(state.reference, action.payload.rows);
            }
        })
            .addMatcher(isPending(getKpiDashboardAnalytics), (state) => {
            state.loading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getKpiAgents), (state) => {
            state.agentsLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isRejected(getKpiDashboardAnalytics, getKpiAgents), (state, action) => {
            if (getKpiDashboardAnalytics.rejected.match(action)) {
                state.loading = false;
            }
            if (getKpiAgents.rejected.match(action)) {
                state.agentsLoading = false;
            }
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { clearKpiDashboard } = kpiDashboardSlice.actions;
export default kpiDashboardSlice.reducer;
